import {
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
  type IPublicClientApplication,
} from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useCallback, useMemo } from "react";

import {
  getGraphPopupRequest,
  getGraphTokenRequest,
  getLoginRequest,
  getPopupRequestForScopes,
  getTokenRequestForScopes,
} from "@/config/msalConfig";
import { getRuntimeConfig } from "@/config/runtimeConfig";
import { decodeJwt } from "@/utils/jwt";

export function useAuth() {
  const { instance, accounts } = useMsal();

  const activeAccount: AccountInfo | null = useMemo(
    () => instance.getActiveAccount() ?? accounts[0] ?? null,
    [instance, accounts],
  );

  const claims = useMemo(() => {
    const rawId = activeAccount?.idToken;
    if (!rawId) return null;
    return decodeJwt(rawId)?.payload ?? null;
  }, [activeAccount]);

  const signIn = useCallback(
    async (returnTo?: string) => {
      try {
        if (returnTo) sessionStorage.setItem("portal:returnTo", returnTo);
      } catch {
        /* noop */
      }
      await instance.loginRedirect(getLoginRequest());
    },
    [instance],
  );

  const signOut = useCallback(async () => {
    const cfg = getRuntimeConfig();
    await instance.logoutRedirect({
      postLogoutRedirectUri: cfg.postLogoutRedirectUri,
    });
  }, [instance]);

  /**
   * Acquire a Microsoft Graph access token. Tries silent first and
   * falls back to a popup when interaction is required (new scopes,
   * expired refresh token, etc.).
   */
  const acquireGraphToken = useCallback(
    async (forceRefresh = false): Promise<AuthenticationResult | null> => {
      const account = instance.getActiveAccount() ?? accounts[0] ?? null;
      if (!account) return null;
      try {
        return await acquireGraphTokenInternal(instance, account, forceRefresh);
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          try {
            return await instance.acquireTokenPopup(getGraphPopupRequest());
          } catch (popupErr) {
            console.error("[auth] graph popup failed", popupErr);
            return null;
          }
        }
        console.error("[auth] graph silent failed", err);
        return null;
      }
    },
    [instance, accounts],
  );

  /**
   * Acquire an access token for an arbitrary Graph scope set (e.g.
   * `Directory.AccessAsUser.All` for the change-password flow).
   * Tries silent first, falls back to popup on interaction_required.
   */
  const acquireToken = useCallback(
    async (
      scopes: string[],
      forceRefresh = false,
    ): Promise<AuthenticationResult | null> => {
      const account = instance.getActiveAccount() ?? accounts[0] ?? null;
      if (!account) return null;
      try {
        return await instance.acquireTokenSilent({
          ...getTokenRequestForScopes(scopes, account),
          forceRefresh,
        });
      } catch (err) {
        if (err instanceof InteractionRequiredAuthError) {
          try {
            return await instance.acquireTokenPopup(
              getPopupRequestForScopes(scopes),
            );
          } catch (popupErr) {
            console.error("[auth] popup failed", popupErr);
            return null;
          }
        }
        console.error("[auth] silent failed", err);
        return null;
      }
    },
    [instance, accounts],
  );

  return {
    instance,
    isAuthenticated: !!activeAccount,
    account: activeAccount,
    claims,
    signIn,
    signOut,
    acquireGraphToken,
    acquireToken,
  };
}

async function acquireGraphTokenInternal(
  instance: IPublicClientApplication,
  account: AccountInfo,
  forceRefresh: boolean,
): Promise<AuthenticationResult> {
  const base = getGraphTokenRequest(account);
  return instance.acquireTokenSilent({ ...base, forceRefresh });
}
